import React, { useState, useEffect } from "react"
import { withPrefix } from "gatsby"
import Layout from "../../components/layout"
import Hero from "../../components/hero"
import MobileOnly from "../../components/mobile-only"
import ReadNext from "../../components/read-next"
import boardActionsData from "../../data/board-actions-data.json"
import boardActionsFilters from "../../data/board-actions-filters.json"
import { slugify } from "../../helpers/strings"
import Image from "../../components/image"
import { AppendixWaypoint, Appendix } from "../../components/appendix"
import { PageColumns, Column } from "../../components/page-column"

const PAGESLUG = "economic-surveillance"

export default function EconomicSurveillance() {
  const [actionsFilter, setActionsFilter] = useState("all") // Accepts a filter slug
  const [actionsTruncated, setActionsTruncated] = useState(true)

  // Process and store the board actions data *once* into datasets based on the filters
  var processedData = []

  boardActionsFilters.forEach(filter => {
    if (filter.slug) {
      if (filter.slug === "all") {
        processedData[filter.slug] = boardActionsData
      } else {
        processedData[filter.slug] = boardActionsData.filter(action =>
          action.categories.includes(filter.title)
        )
      }
    }
  })

  // Update the current dataset according to the actionsFilter state
  var filteredData = processedData[actionsFilter]

  //
  // Event Handlers
  //

  function handleSelectChange(e) {
    setActionsFilter(e.target.value)
  }

  // Hide the load more button if less than 7 items
  useEffect(() => {
    if (filteredData.length <= 6) {
      setActionsTruncated(false)
    } else {
      setActionsTruncated(true)
    }
  }, [actionsFilter])

  return (
    <Layout>
      <section className="page page--pullBottom page--margin-large">
        <Hero
          title="Economic Surveillance"
          image="what-we-do/economic-surveillance/hero.jpg"
        />

        <div className="page__intro container md:container--small">
          <p className="dropcap">
            <b>
              Through surveillance, the IMF monitors the international monetary
              system and global economic developments
            </b>{" "}
            and engages in a health check of the economic and financial policies
            of its 190 member countries. In addition, the IMF highlights
            possible stability risks to its member countries and advises their
            governments on potential policy adjustments, enabling the
            international monetary system to achieve its goal of facilitating
            the exchange of goods, services, and capital among countries,
            thereby sustaining sound economic growth. The IMF advises on the
            policies of each member country through “bilateral surveillance” and
            provides analysis of the international monetary system and global
            and regional economic developments through “multilateral
            surveillance.” In practice, these two aspects of surveillance inform
            one another, ensuring a more comprehensive, consistent analysis of
            “spillovers”—how one country’s policies affect other countries.
          </p>
        </div>

        <PageColumns>
          <Column type="text">
            <h2>Bilateral surveillance</h2>
            <AppendixWaypoint page={PAGESLUG} id={1} />
            <p>
              Article IV consultations consist of a two-way policy dialogue
              between the IMF and country authorities and cover a range of
              important macro-critical issues: financial, fiscal, foreign
              exchange, monetary, and structural. Because of the unprecedented
              demand for financing and support at the onset of the COVID-19
              crisis, the IMF’s work priorities had to be shifted, its
              procedures streamlined, and its staff redeployed. Staff work on
              Article IV consultations and the FSAP—the main vehicles for
              bilateral surveillance—was temporarily put on hold as a result,
              and consultation cycles were extended to ensure the postponement
              had no impact on IMF members’ surveillance obligations.
            </p>
            <p>
              Article IV consultations and assessments under the FSAP have since
              resumed remotely, but because of the pandemic, the IMF conducted
              only 36 Article IV consultations in FY 2021 (see{" "}
              <a href={`${withPrefix("/")}downloads/web-tables.pdf`}>
                Web Table 2
              </a>
              )—less than half the usual number of consultations held annually—
              and eight Financial System Stability Assessments under the FSAP.
            </p>
            <MobileOnly>
              <Image
                path="what-we-do/economic-surveillance/bilateral-surveillance.jpg"
                alt="Family riding on a motorcycle"
              />
            </MobileOnly>
            <p>
              Follow-up work as part of the 2021 Comprehensive Surveillance
              Review will help strengthen systemic financial risk analysis and
              macroprudential policy advice in Article IV consultations, as
              bilateral surveillance ramps up.{" "}
            </p>
          </Column>

          <Column type="media">
            <Image
              path="what-we-do/economic-surveillance/bilateral-surveillance.jpg"
              alt="Family riding on a motorcycle"
            />
          </Column>
        </PageColumns>

        {/* The 2021 Comprehensive Surveillance Review  */}
        <PageColumns modifiers="md:pull--large">
          <Column type="media"></Column>

          <Column type="text">
            <h2>The 2021 Comprehensive Surveillance Review</h2>
            <AppendixWaypoint page={PAGESLUG} id={3} />
            <p>
              Work on the 2021 Comprehensive Surveillance Review was underway
              during FY 2021 (and was completed in May 2021, after this report
              was finalized). This review aims to strengthen IMF surveillance to
              help countries navigate the challenges of the next decade by
              better integrating aspects of the IMF’s work into surveillance,
              from macro-financial analysis to climate to capacity development.
              The IMF will also draw on new technologies, data, and partnerships
              to support more timely, more focused, and better-informed policy
              advice.
            </p>
          </Column>
        </PageColumns>

        {/* Multilateral surveillance */}
        <PageColumns modifiers="md:pull--large lg:pull--xl">
          <Column type="text">
            <h2>Multilateral surveillance</h2>
            <p>
              As part of its multilateral surveillance, the IMF issues biannual
              reports and updates on the latest global economic developments:
              the <i>World Economic Outlook</i>, the{" "}
              <i>Global Financial Stability Report</i>, and the{" "}
              <i>Fiscal Monitor</i>. Interim updates are also provided when
              warranted. In addition, as part of an ongoing effort to provide a
              rigorous and candid assessment of global excess imbalances and
              their causes, the <i>External Sector Report</i> is published
              annually. Article IV consultations and Financial System Stability
              Assessments under the FSAP also discuss issues related to
              multilateral surveillance, where relevant.
            </p>
          </Column>

          <Column type="media"></Column>
        </PageColumns>

        {/* Policy advice  */}
        <PageColumns modifiers="md:pull--large lg:pull--xl">
          <Column type="media"></Column>

          <Column type="text">
            <h2>Policy advice</h2>
            <p>
              The IMF Executive Board discusses all aspects of the IMF’s work,
              from Article IV consultations to policy issues relevant to the
              global economy. The Board carries out its work largely on the
              basis of policy papers prepared by IMF management and staff. In FY
              2021, the IMF published 54 of these policy papers externally.
            </p>
          </Column>
        </PageColumns>

        {/* Policy Papers */}
        <div className="container container--full lg:container--large">
          <div
            id="board-actions"
            className={
              actionsTruncated
                ? `board-actions board-actions--truncated`
                : "board-actions"
            }
          >
            <header>
              <h2 className="title--l md:title--xl">Policy Papers</h2>
              <div className="board-actions__filter-wrapper">
                <header
                  id="board-actions__filter__header"
                  className="title--micro title--light title--uppercase"
                >
                  Filter By
                </header>
                <select
                  name="filters"
                  id="filters-select"
                  className="board-actions__filter"
                  value={actionsFilter}
                  onChange={handleSelectChange}
                  aria-labelledby="board-actions__filter__header"
                  aria-label="Filter the board actions"
                  aria-controls="board-actions"
                >
                  {boardActionsFilters.map((filter, index) => {
                    return (
                      <option
                        key={"filter-select-" + filter.slug + index}
                        value={filter.slug}
                      >
                        {filter.title}
                      </option>
                    )
                  })}
                </select>
              </div>
            </header>
            <ul className="board-actions__items">
              {filteredData.map((action, index) => {
                // Action id must be totally unique or the list will not render/filter correctly
                let id = `board-actions-item-${slugify(action.title)}-${slugify(
                  action.date
                )}-${
                  action.series_number && slugify(action.series_number)
                }-${index}`

                return <BoardActionItem key={id} id={id} action={action} />
              })}
            </ul>
            {actionsTruncated && (
              <button
                className="cta cta--primary"
                onClick={() => setActionsTruncated(false)}
              >
                Load More
              </button>
            )}
          </div>
        </div>
      </section>

      <ReadNext sectionID="what-we-do" pageID="lending" />
      <Appendix page={PAGESLUG} />
    </Layout>
  )
}

//
// Subcomponents
//

function BoardActionItem({ id, action }) {
  const [textIsTruncated, setTextIsTruncated] = useState(true)

  var truncatedText = action.summary && truncateText(action.summary)

  function truncateText(str) {
    return str.split(" ").splice(0, 25).join(" ")
  }

  function formatDate(dateString) {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ]

    let dateStringArray = dateString.split("/")

    return `${months[dateStringArray[0] - 1]} ${dateStringArray[1]}, ${
      dateStringArray[2]
    }`
  }

  function handleReadMoreButtonClick() {
    setTextIsTruncated(false)
  }

  return (
    <li id={id} className="board-actions__items__item">
      <div className="board-actions__items__item__date">
        <p className="title--xs">{formatDate(action.date)}</p>
        <div className="board-actions__items__item__date__line"></div>
      </div>
      <div className="board-actions__items__item__content">
        <header>
          <p className="board-actions__items__item__content__category title--xs">
            {action.categories}
          </p>
          <h3 className="title--r md:title--m">
            {action.link ? (
              <a href={action.link} target="_blank" rel="noreferrer">
                {action.title}
              </a>
            ) : (
              action.title
            )}
          </h3>
        </header>

        {action.summary?.length &&
          (truncatedText?.length &&
          action.summary?.length > truncatedText?.length ? (
            <>
              <p className={textIsTruncated ? "" : "util__hidden"}>
                {truncatedText}…{" "}
                <button
                  className="board-actions__items__item__content__read-more-button"
                  type="button"
                  onClick={handleReadMoreButtonClick}
                  aria-controls={id}
                >
                  Read More
                </button>
              </p>
              <p className={textIsTruncated ? "util__hidden" : ""}>
                {action.summary}
              </p>
              {action.press_release && (
                <p className={textIsTruncated ? "util__hidden" : ""}>
                  For more information, read the{" "}
                  <a
                    href={action.press_release}
                    target="_blank"
                    rel="noreferrer"
                  >
                    press release
                  </a>
                  .
                </p>
              )}
            </>
          ) : (
            <>
              <p>{action.summary}</p>
              {action.press_release && (
                <p>
                  For more information, read the{" "}
                  <a
                    href={action.press_release}
                    target="_blank"
                    rel="noreferrer"
                  >
                    press release
                  </a>
                  .
                </p>
              )}
            </>
          ))}
      </div>
    </li>
  )
}
